import { useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useLiveUpdates } from '../api/live.js'
import { useClearSession, useSession } from '../api/session.js'
import { Icon, type IconName } from '../components/icons.js'
import { Wordmark } from '../components/Logo.js'
import { ThemeToggle } from '../components/theme.js'

type NavItem = { to: string; label: string; icon: IconName; end?: boolean }

/** What the Disposition works with every day (Workshop 23.10.). */
const NAV: NavItem[] = [
  { to: '/aufgaben', label: 'Aufgaben', icon: 'list' },
  { to: '/plaene', label: 'Aufgabenpläne', icon: 'repeat' },
  { to: '/mitarbeiter', label: 'Mitarbeiter', icon: 'users' },
  { to: '/objekte', label: 'Objekte', icon: 'tree' },
  { to: '/legionellen', label: 'Legionellen', icon: 'check' },
  { to: '/vorlagen', label: 'Leistungen', icon: 'tools' },
  { to: '/maengel', label: 'Mängel und Anliegen', icon: 'warning' },
  { to: '/uebergabe', label: 'Übergabe', icon: 'handover' },
]

/** Everything else stays, one click further away. */
const MORE: NavItem[] = [
  { to: '/uebersicht', label: 'Übersicht', icon: 'home' },
  { to: '/nachweise', label: 'Nachweise', icon: 'document' },
  { to: '/auswertungen', label: 'Auswertungen', icon: 'table' },
  { to: '/qualitaet', label: 'Qualität', icon: 'star' },
]
const MORE_KEY = 'hauswerk.nav.more'

const PIN_KEY = 'hauswerk.nav.pinned'

function readPinned(): boolean {
  try {
    return localStorage.getItem(PIN_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * The sidebar is a narrow rail of icons by default and opens on hover, so
 * the work area, above all the object explorer, gets the width. Whoever
 * prefers the labels pins it open; that choice is remembered per browser.
 */
export function BackofficeLayout() {
  const { data: session } = useSession()
  const { connected } = useLiveUpdates()
  const clearSession = useClearSession()
  const navigate = useNavigate()
  const [pinned, setPinned] = useState(readPinned)
  const location = useLocation()
  const inMore = MORE.some((item) => location.pathname.startsWith(item.to))
  const [moreOpen, setMoreOpen] = useState(() => {
    try {
      return localStorage.getItem(MORE_KEY) === '1'
    } catch {
      return false
    }
  })
  const showMore = moreOpen || inMore
  function toggleMore() {
    setMoreOpen((current) => {
      try {
        localStorage.setItem(MORE_KEY, current ? '0' : '1')
      } catch {
        /* per-browser convenience only */
      }
      return !current
    })
  }

  function togglePinned() {
    setPinned((current) => {
      try {
        localStorage.setItem(PIN_KEY, current ? '0' : '1')
      } catch {
        /* per-browser convenience only */
      }
      return !current
    })
  }

  return (
    <div className={`bo ${pinned ? 'nav-pinned' : 'nav-rail'}`}>
      <nav className="bo-nav">
        <div className="bo-bar">
          <div className="bo-brand">
            <Wordmark size={30} tone="light" subtitle="Nachweis & Aufgaben" />
          </div>
          <button
            type="button"
            className="nav-pin"
            onClick={togglePinned}
            title={pinned ? 'Leiste einklappen' : 'Leiste offen lassen'}
            aria-pressed={pinned}
            aria-label={pinned ? 'Leiste einklappen' : 'Leiste offen lassen'}
          >
            <Icon name={pinned ? 'chevronLeft' : 'pin'} size={16} />
          </button>
          <div className="bo-bar-actions">
            <span
              className="live-dot compact"
              title={connected ? 'Änderungen erscheinen sofort' : 'Keine Live-Verbindung'}
            >
              <i className={connected ? '' : 'off'} />
            </span>
          </div>
        </div>

        {/* Its own scroll container: on a phone the links become a swipeable row
            instead of forcing the whole page wider than the screen. */}
        <div className="bo-links">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} title={item.label}>
              <Icon name={item.icon} />
              <span className="nav-label">{item.label}</span>
            </NavLink>
          ))}
          <button type="button" className={`nav-more ${showMore ? 'open' : ''}`} onClick={toggleMore} aria-expanded={showMore} title="Sonstiges">
            <Icon name="grid" />
            <span className="nav-label">Sonstiges</span>
            <Icon name={showMore ? 'chevronDown' : 'chevronRight'} size={14} className="nav-more-caret" />
          </button>
          {showMore &&
            MORE.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} title={item.label} className="nav-sub">
                <Icon name={item.icon} />
                <span className="nav-label">{item.label}</span>
              </NavLink>
            ))}
          <NavLink to="/einstellungen" title="Einstellungen">
            <Icon name="gear" />
            <span className="nav-label">Einstellungen</span>
          </NavLink>
        </div>

        <div className="spacer" />
        <div
          className="live-dot full"
          title={connected ? 'Änderungen erscheinen sofort' : 'Keine Live-Verbindung, Daten werden regelmäßig neu geladen'}
        >
          <i className={connected ? '' : 'off'} />
          {connected ? 'Live' : 'Nicht verbunden'}
        </div>
        <ThemeToggle />
        <div className="who">
          <strong>{session?.user.name}</strong>
          {session?.tenant?.name}
        </div>
        <button
          type="button"
          className="btn ghost small"
          style={{ color: '#cfe1d8', justifyContent: 'flex-start', gap: 10, paddingLeft: 12 }}
          onClick={async () => {
            await clearSession()
            navigate('/login', { replace: true })
          }}
          title="Abmelden"
        >
          <Icon name="logout" />
          <span className="nav-label">Abmelden</span>
        </button>
      </nav>
      <main className="bo-main">
        <Outlet />
      </main>
    </div>
  )
}

export function PageHead({
  title,
  subtitle,
  action,
}: {
  title: string
  subtitle?: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <div className="bo-head spread">
      <div>
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {action}
    </div>
  )
}
